import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fdateShort } from "@/lib/format";
import { PageHeader, Loader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Crown, Plus, Trash2, Copy, Eye, Pencil, Check, Newspaper } from "lucide-react";
import StationeryCanvas from "@/components/stationery/StationeryCanvas";
import { GAZ_TEMPLATES, GAZ_W, GAZ_H, buildTemplate } from "@/lib/gazette";
import GazetteEditor from "./GazetteEditor";

export default function Gazette() {
  const { wedding, hasFeature, premium } = useAuth();
  const wid = wedding?.wedding?.id;
  const w = wedding?.wedding;
  const navigate = useNavigate();
  const premiumOn = hasFeature("gazette");
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);

  const ctx = useCallback(() => ({ names: w?.names, dateFr: fdateShort(w?.wedding_date), city: w?.city,
    siteUrl: `${window.location.origin}/w/${w?.slug}` }), [w]);

  const load = useCallback(async () => {
    const { data } = await api.get(`/weddings/${wid}/gazettes`); setItems(data);
  }, [wid]);
  useEffect(() => { if (wid) load(); }, [wid, load]);

  const create = async (tpl) => {
    if (!premiumOn) { navigate("/app/premium"); return; }
    const pages = buildTemplate(tpl.id, ctx());
    try {
      const { data } = await api.post(`/weddings/${wid}/gazettes`, { title: tpl.name, template_id: tpl.id, format: "A4", pages });
      setEditing(data); load();
    } catch (e) { if (e.response?.status === 402) navigate("/app/premium"); else toast.error("Erreur"); }
  };
  const openG = (g) => { if (!premiumOn) { navigate("/app/premium"); return; } setEditing({ ...g, preview: false }); };
  const previewG = (g) => setEditing({ ...g, preview: true });
  const dup = async (g) => { try { await api.post(`/weddings/${wid}/gazettes/${g.id}/duplicate`); toast.success("Dupliquée"); load(); } catch { navigate("/app/premium"); } };
  const del = async (g) => { if (!window.confirm(`Supprimer « ${g.title} » ?`)) return; await api.delete(`/weddings/${wid}/gazettes/${g.id}`); toast.success("Supprimée"); load(); };

  if (!items) return <Loader />;
  if (editing) return <GazetteEditor wid={wid} gazette={editing} startPreview={editing.preview} onBack={() => { setEditing(null); load(); }} />;

  return (
    <div>
      <PageHeader eyebrow="Créer · Premium" title="La Gazette des Mariés"
        description="Un vrai mini-journal de mariage personnalisable : histoire, programme, anecdotes, menu… exportable en PDF."
        actions={premium?.active ? <span className="inline-flex items-center gap-1 text-sm text-sage"><Crown className="w-4 h-4" /> Premium actif</span> : null} />

      {!premiumOn && (
        <div className="rounded-2xl p-6 sm:p-8 mb-8" style={{ background: "linear-gradient(120deg,#1c1b1a,#3a332a)" }} data-testid="gazette-premium-cta">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-1 text-champagne text-xs uppercase tracking-wide mb-2"><Crown className="w-3.5 h-3.5" /> Fonction Premium</div>
              <div className="font-serif text-2xl text-white">Débloquez la Gazette des Mariés</div>
              <p className="text-white/70 text-sm mt-2">Créez votre journal de mariage, ajoutez vos photos et vos textes, et exportez-le en PDF haute qualité pour l'imprimer où vous voulez. Découvrez ci-dessous les modèles inclus.</p>
              <ul className="grid sm:grid-cols-2 gap-1.5 mt-4 text-sm text-white/80">
                {["6 modèles graphiques distincts", "Éditeur multi-pages complet", "Blocs prêts à l'emploi", "Export PDF & images HD"].map((b, i) => (
                  <li key={i} className="flex items-center gap-2"><Check className="w-4 h-4 text-sage" /> {b}</li>))}
              </ul>
            </div>
            <Button onClick={() => navigate("/app/premium")} data-testid="gazette-upgrade-btn"
              className="rounded-full bg-champagne text-stone-900 hover:bg-champagne/90 shrink-0">Passer Premium</Button>
          </div>
        </div>
      )}

      {premiumOn && items.length > 0 && (
        <div className="mb-10">
          <div className="eyebrow mb-4">Vos Gazettes</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {items.map((g) => (
              <div key={g.id} className="group" data-testid={`gazette-${g.id}`}>
                <div className="rounded-lg overflow-hidden border bg-white flex items-center justify-center p-2 relative">
                  {g.pages?.[0]
                    ? <StationeryCanvas canvas={{ w: GAZ_W, h: GAZ_H, bg: g.pages[0].bg, layers: g.pages[0].layers }} scale={150 / GAZ_W} />
                    : <div className="h-52 flex items-center justify-center text-stone-300"><Newspaper className="w-8 h-8" /></div>}
                  <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full bg-white/90 border">{g.status === "done" ? "Terminée" : "Brouillon"}</span>
                </div>
                <div className="mt-2 text-sm font-medium truncate">{g.title}</div>
                <div className="text-xs text-stone-400">{g.pages?.length || 0} p. · {fdateShort(g.updated_at)}</div>
                <div className="flex gap-1 mt-2">
                  <IconAction icon={Pencil} onClick={() => openG(g)} testid={`edit-${g.id}`} title="Modifier" />
                  <IconAction icon={Eye} onClick={() => previewG(g)} testid={`preview-${g.id}`} title="Prévisualiser" />
                  <IconAction icon={Copy} onClick={() => dup(g)} testid={`dup-${g.id}`} title="Dupliquer" />
                  <IconAction icon={Trash2} onClick={() => del(g)} testid={`del-${g.id}`} title="Supprimer" danger />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="eyebrow mb-4">Modèles</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
        {GAZ_TEMPLATES.map((t) => (
          <button key={t.id} onClick={() => create(t)} data-testid={`gaz-template-${t.id}`} className="group text-left">
            <div className="rounded-lg overflow-hidden border bg-white flex items-center justify-center p-2 group-hover:shadow-xl transition-shadow relative">
              <StationeryCanvas canvas={{ w: GAZ_W, h: GAZ_H, ...firstPage(t, ctx()) }} scale={190 / GAZ_W} />
              {!premiumOn && <div className="absolute top-2 right-2"><span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-stone-900/80 text-champagne"><Crown className="w-2.5 h-2.5" /> Premium</span></div>}
            </div>
            <div className="mt-2 flex items-center justify-between">
              <div><div className="text-sm font-medium">{t.name}</div><div className="text-xs text-stone-400">{t.mood}</div></div>
              <Plus className="w-4 h-4 text-stone-400 group-hover:text-champagne" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function firstPage(t, ctx) { const p = buildTemplate(t.id, ctx)[0]; return { bg: p.bg, layers: p.layers }; }
function IconAction({ icon: Icon, onClick, title, danger, testid }) {
  return <button onClick={onClick} title={title} data-testid={testid}
    className={`flex-1 h-8 rounded-md border flex items-center justify-center hover:bg-accent ${danger ? "hover:text-destructive" : ""}`}>
    <Icon className="w-3.5 h-3.5" /></button>;
}
